"use client";

import cn from "classnames";
import { FC, Fragment, ReactNode, useCallback, useMemo } from "react";
import InteractiveTable, {
  COL_COLOR_MAPPING,
  InteractiveTableProps,
} from "../InteractiveTable";
import { OutputType } from "../InteractiveTable/InteractiveTable.types";
import TableControlSection, {
  TableControlSectionProps,
} from "../InteractiveTable/TableControlSection";
import InteractiveTableCell from "../InteractiveTableCell";
import { Challenge } from "../InteractiveTableSteps/ChallengeDropdownSection";

export type ConfirmationData = {
  name: string;
  company: string;
};

export type MaturityModelProps = {
  interactiveTableSteps: ReactNode;
  interactiveTable: Omit<
    InteractiveTableProps,
    "rowCellsChildren" | "clearModelSlot"
  >;
  tableCells?: (OutputType | null)[][];
  tableControls: Omit<TableControlSectionProps, "clearModel">;
  hasSelectedItems: boolean;
  selectedChallenge: Challenge | null;
  resetCounter: number;
  onSelectionValidChange?: (hasValidSelection: boolean) => void;
  onChallengeChange?: (challenge: Challenge | null) => void;
  onClearModel?: () => void;
};

const MaturityModel: FC<MaturityModelProps> = ({
  interactiveTableSteps,
  interactiveTable,
  tableCells,
  tableControls,
  selectedChallenge,
  resetCounter,
  onSelectionValidChange,
  onClearModel,
}) => {
  const handleSelectionValidChange = useCallback(
    (hasValidSelection: boolean) => {
      onSelectionValidChange?.(hasValidSelection);
    },
    [onSelectionValidChange],
  );
  const handleClearModel = useCallback(() => {
    onClearModel?.();
  }, [onClearModel]);

  const rowsContent: ReactNode[][] = useMemo(
    () =>
      interactiveTable?.rowHeader?.map(
        (row, idx) =>
          tableCells?.[idx]?.map((cell, colIndex) => {
            if (idx !== 0 && !interactiveTable?.colHeader?.[colIndex]?.hasCards)
              return (
                <Fragment
                  key={`maturity-model-empty-cell-${row.categoryKey}-${colIndex}`}
                ></Fragment>
              );
            return !interactiveTable?.colHeader?.[colIndex]?.hasCards ? (
              <div
                key={`maturity-model-text-cell-${row.categoryKey}-${colIndex}`}
                className={cn(
                  "text-body-xs text-swdc-gray-400 row-span-5 mx-2 my-3 flex items-center rounded-md px-2 text-left",
                  COL_COLOR_MAPPING?.[colIndex]?.background,
                )}
              >
                {interactiveTable.colHeader?.[colIndex]?.content || ""}
              </div>
            ) : (
              <div
                key={`maturity-model-interactive-cell-${row.categoryKey}-${colIndex}`}
                className={cn(
                  "mt-3 mr-2 flex items-center justify-center",
                  idx + 1 === (interactiveTable.rowHeader?.length || 0) &&
                    "mb-3",
                )}
              >
                {cell && (
                  <InteractiveTableCell
                    header={cell.header}
                    description={cell.description}
                    selection={cell.selection}
                    horizontalCategory={{
                      categoryGroup: cell.horizontalCategory.categoryGroup,
                      colIndex: colIndex,
                      category:
                        interactiveTable.colHeader[colIndex]?.header.value ??
                        "",
                    }}
                    verticalCategory={{
                      categoryGroup: cell.verticalCategory.categoryGroup,
                      category: row.value,
                    }}
                    appearance={
                      !selectedChallenge?.key
                        ? "default"
                        : cell.tableChallenges.includes(selectedChallenge.key)
                          ? "highlighted"
                          : "disabled"
                    }
                    setValid={handleSelectionValidChange}
                    resetCounter={resetCounter}
                  />
                )}
              </div>
            );
          }) ?? [],
      ) ?? [],
    [
      interactiveTable?.rowHeader,
      interactiveTable?.colHeader,
      tableCells,
      selectedChallenge,
      resetCounter,
      handleSelectionValidChange,
    ],
  );
  const clearModelSlot = (
    <TableControlSection
      title={tableControls?.title}
      options={tableControls?.options}
      clearButton={tableControls?.clearButton}
      clearModal={tableControls?.clearModal}
      clearModel={handleClearModel}
      analytics={tableControls.analytics}
    />
  );
  return (
    <div className="mx-auto">
      {interactiveTableSteps}
      <div className="relative w-full overflow-hidden">
        <div className="relative bg-white">
          <InteractiveTable
            colTitle={interactiveTable?.colTitle ?? ""}
            rowTitle={interactiveTable?.rowTitle ?? ""}
            colHeader={interactiveTable?.colHeader ?? []}
            rowHeader={interactiveTable?.rowHeader ?? []}
            rowCellsChildren={rowsContent}
            clearModelSlot={clearModelSlot}
          />
          <div className="absolute top-0 -z-10" id="itsm-table-image-block" />
        </div>
      </div>
    </div>
  );
};

export default MaturityModel;
